import { useGetSource } from 'api/HightouchAPI';
import { PAGE_TITLE } from 'modules/dataWarehouse/constants';
import { useDataQueryContext } from 'modules/dataWarehouse/contexts';
import { addNegativeToastWithErrorId } from 'modules/toastContainer';
import { useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useClickAway } from 'react-use';
import styled from 'styled-components';
import { DRAWER_SIZES } from 'ui/workflows/drawer/constants';
import { Takeover, TakeoverTestIds } from 'ui/workflows/takeover';
import { TakeoverBody } from './TakeoverBody';

export const EDIT_SOURCE_TEXT = {
  errorTitle: 'We are having trouble editing a source right now.',
  errorSubtitle: 'Sorry about that! Please try again later.',
  id: 'edit-source',
  iframeName: 'editSourceIframe',
  iframeTitle: 'Edit source',
  takeoverTitle: PAGE_TITLE,
};

const StyledWrapper = styled.div`
  flex-grow: 1;
`;

export type EditSourceTakeoverProps = {
  id: number;
  onClose: () => void;
};

export const EditSourceTakeover = ({ id, onClose }: EditSourceTakeoverProps) => {
  const dispatch = useDispatch();
  const { refetchSyncs } = useDataQueryContext();
  const bodyRef = useRef(null);
  useClickAway(bodyRef, (e) => {
    if (e.target instanceof HTMLElement && e.target.id === TakeoverTestIds.component) {
      onClose();
    }
  });

  const { data, error, isLoading } = useGetSource(id, {
    onError: () => {
      dispatch(addNegativeToastWithErrorId('Unable to find source.', 'useGetSource', error));
    },
    retry: false,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    // get latest syncs when unmounting because Hightouch events
    // are not triggered when the takeover is closed
    return () => refetchSyncs();
  }, []);

  return (
    <Takeover
      isLoading={isLoading}
      isMainApp
      isOverlay
      noPadding
      onClose={onClose}
      size={DRAWER_SIZES.LARGE}
      title={EDIT_SOURCE_TEXT.takeoverTitle}
      Body={
        <StyledWrapper ref={bodyRef}>
          <TakeoverBody
            errorSubtitle={EDIT_SOURCE_TEXT.errorSubtitle}
            errorTitle={EDIT_SOURCE_TEXT.errorTitle}
            hasError={!!error}
            id={EDIT_SOURCE_TEXT.id}
            name={EDIT_SOURCE_TEXT.iframeName}
            title={EDIT_SOURCE_TEXT.iframeTitle}
            url={data?.url}
          />
        </StyledWrapper>
      }
    />
  );
};
